/**
 * The reader's "Modeled Water Use" table (`SiteRagReport.waterScreeningView`)
 * is a projection of the saved section text (`SiteRagReport.waterScreening`):
 * both come from one builder at generation time, and DOCX, PPTX and the PDF
 * appendix print the text. Anything that later edits the saved text (a
 * post-generation rewrite, a manual audit fix) leaves the view behind, and the
 * reader would then show different words than every export.
 *
 * The reader therefore lays out the view only while every string it prints is
 * still in the saved text; otherwise it falls back to the saved text itself.
 */
import type { WaterScreeningView } from '../../types/siteRag';

/** Every string the reader prints from the view, in reading order. */
export function waterScreeningViewStrings(view: WaterScreeningView): string[] {
  return [
    view.scope,
    view.catalogVersion,
    view.lead,
    ...view.basis,
    ...view.rows.flatMap(row => [
      row.label,
      row.applicability,
      row.boundary,
      row.flowBasis,
      row.averageDay,
      row.annual,
      row.fullLoad,
      row.conditional?.value,
      row.conditional?.qualifier,
      row.assist?.label,
      row.assist?.basis,
      row.assist?.averageDay,
      row.assist?.annual,
      row.assist?.activeHours,
      row.assist?.peakHour,
      ...row.details,
    ]),
    ...view.caveats,
    ...view.notes,
    ...view.sources.flatMap(source => [source.citation, source.url]),
  ].filter((value): value is string => typeof value === 'string' && value.length > 0);
}

/** True when the saved text still contains every string the view would print. */
export function isWaterScreeningViewFaithful(view: WaterScreeningView, savedText: string | undefined): boolean {
  if (!savedText?.trim()) return false;
  return waterScreeningViewStrings(view).every(value => savedText.includes(value));
}
